import { Component, Inject, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Banner, BannerService } from '@uzurv/clara';
import {
  AMPLITUDE_CONFIGURATION,
  AmplitudeConfiguration,
  AppConstants,
  BugsnagService,
  isNullOrUndefined,
  ReleasePlatformConstants,
  SystemEvent,
  SystemEventConstants,
  SystemEventsService
} from '@uzurv/core';
import { AuthDataService } from '@uzurv/http';
import { Observable } from 'rxjs';
import { APP_CONFIGURATION, AppConfiguration } from './app-configuration';
import { AppSetupService } from './app-setup.service';
import { State, Store } from '@ngrx/store';
import { LogRocketService } from '../services/log-rocket/log-rocket.service';
import { LegacyFeatureFlagService } from '../services/legacy-feature-flag/legacy-feature-flag.service';
import * as FeatureFlagActions from '../store/feature-flag/feature-flag.actions';
import { AmplitudeAnalyticsService } from '../services/amplitude/amplitude-analytics.service';
import { FeatureFlagState } from '../store/feature-flag';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [AppSetupService]
})
export class AppComponent implements OnInit {
  bannerInterval: number;
  banners$: Observable<Array<Banner>>;

  constructor(
    private amplitudeAnalyticsService: AmplitudeAnalyticsService,
    private appSetupService: AppSetupService,
    private authDataService: AuthDataService,
    private bannerService: BannerService,
    private bugsnagService: BugsnagService,
    private domSanitizer: DomSanitizer,
    private featureFlagStore: Store<State<FeatureFlagState>>,
    private logRocketService: LogRocketService,
    private matIconRegistry: MatIconRegistry,
    private route: ActivatedRoute,
    private router: Router,
    private store$: Store,
    private systemEventsService: SystemEventsService,
    private legacyFeatureFlagService: LegacyFeatureFlagService,
    @Inject(AMPLITUDE_CONFIGURATION) protected amplitudeConfiguration: AmplitudeConfiguration,
    @Inject(APP_CONFIGURATION) private appConfiguration: AppConfiguration
  ) {}

  ngOnInit() {
    this.authDataService.logoutEvents.subscribe(() => {
      this.bugsnagService.setUser(null);
      this.store$.dispatch(FeatureFlagActions.StopPollingFeatureFlags());

      window.clearInterval(this.bannerInterval);
    });

    this.authDataService.loginEvents.subscribe(() => {
      this.bugsnagService.setUser(this.authDataService.currentUser);

      this.initBannerInterval();

      this.legacyFeatureFlagService.getFeatureFlags();
    });

    if (this.authDataService.isLoggedIn) {
      this.initBannerInterval();
    }

    this.appSetupService.setupApp();
    this.systemEventsService.getSystemEvents().subscribe(this.systemEventHandler);

    this.logRocketService.init();

    this.amplitudeAnalyticsService.init(this.amplitudeConfiguration.amplitudeSessionCaptureEnabled).then(() => {
      this.featureFlagStore.dispatch(FeatureFlagActions.InitializeFeatureFlags());
    });

    this.matIconRegistry.addSvgIcon('uz_home', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/home-icon.svg'));
    this.matIconRegistry.addSvgIcon('uz_itinerary', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/itinerary-icon.svg'));
    this.matIconRegistry.addSvgIcon('uz_rider', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/rider-icon.svg'));
    this.matIconRegistry.addSvgIcon('uz_rides', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/rides-icon.svg'));
    this.matIconRegistry.addSvgIcon('uz_settings', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/settings-icon.svg'));
    this.matIconRegistry.addSvgIcon('uz_dots', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/dots-background.svg'));
    this.matIconRegistry.addSvgIcon('uz_steering_wheel', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/steering_wheel.svg'));
    this.matIconRegistry.addSvgIcon('uz_cancel', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/cancel.svg'));
    this.matIconRegistry.addSvgIcon('uz_drop_off', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/wav-drop-off.svg'));
    this.matIconRegistry.addSvgIcon('uz_pick_up', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/wav-pick-up.svg'));
    this.matIconRegistry.addSvgIcon('uz_trip_dots', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/trip-dots.svg'));

    // This naming convention is deliberately different. We're going to move toward a common uz-icon set with consistent naming
    // Let's keep this alphabetical for sanity's sake
    this.matIconRegistry.addSvgIcon(
      'uz-driver-assigned',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-driver-assigned.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'uz-driver-not-assigned',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-driver-not-assigned.svg')
    );
    this.matIconRegistry.addSvgIcon('uz-back', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-back.svg'));
    this.matIconRegistry.addSvgIcon('uz-calendar', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-calendar.svg'));
    this.matIconRegistry.addSvgIcon('uz-check', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-check.svg'));
    this.matIconRegistry.addSvgIcon(
      'uz-in-progress',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-in-progress.svg')
    );
    this.matIconRegistry.addSvgIcon('uz-lyft', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/lyft.svg'));
    this.matIconRegistry.addSvgIcon('uz-note', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-note.svg'));
    this.matIconRegistry.addSvgIcon(
      'uz-note-add-outline',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-note-add-outline.svg')
    );
    this.matIconRegistry.addSvgIcon('uz-pending', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-pending.svg'));
    this.matIconRegistry.addSvgIcon('uz-rider', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-rider.svg'));
    this.matIconRegistry.addSvgIcon('uz-driver', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-driver.svg'));
    this.matIconRegistry.addSvgIcon('uz-provider', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-provider.svg'));
    this.matIconRegistry.addSvgIcon(
      'uz-person-delete',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-person-delete.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'uz-providers',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-providers.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'uz-providers-small',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-providers-small.svg')
    );
    this.matIconRegistry.addSvgIcon('uz-route', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-route.svg'));
    this.matIconRegistry.addSvgIcon('uz-target', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-target.svg'));
    this.matIconRegistry.addSvgIcon(
      'uz-user-checked',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-user-checked.svg')
    );
    this.matIconRegistry.addSvgIcon('uz-user', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-user.svg'));
    this.matIconRegistry.addSvgIcon(
      'uz-on-demand-circle',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-on-demand-circle.svg')
    );
    this.matIconRegistry.addSvgIcon('uz-save', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-save.svg'));
    this.matIconRegistry.addSvgIcon(
      'uz-send-to-back',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-send-to-back.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'uz-send-to-front',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-send-to-front.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'uz-swap-horizontal',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-swap-horizontal.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'uz-search-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-search-icon.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'uz-back-arrow',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-back-arrow.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'uz-select-arrow',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-select-arrow.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'uz-edit-button',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-edit-button.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-edit-outline-button',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-edit-outline-button.svg')
    );

    this.matIconRegistry.addSvgIcon('uz-warning', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-warning.svg'));

    this.matIconRegistry.addSvgIcon(
      'uz-warning-outline',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-warning-outline.svg')
    );

    this.matIconRegistry.addSvgIcon('uz-close', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-close.svg'));

    this.matIconRegistry.addSvgIcon(
      'uz-repeat-recur-cycle',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-repeat-recur-cycle.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-rider-position',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-rider-position.svg')
    );

    this.matIconRegistry.addSvgIcon('uz-refresh', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-refresh.svg'));

    this.matIconRegistry.addSvgIcon(
      'uz-calendar-today',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-calendar-today.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-cancel-busy-calendar-date',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-cancel-busy-calendar-date.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-alert-circle-outline',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-alert-circle-outline.svg')
    );
    this.matIconRegistry.addSvgIcon('uz-dispatch', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-dispatch.svg'));
    this.matIconRegistry.addSvgIcon(
      'uz-dispatch-small',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-dispatch-small.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'uz-info-outline',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-info-outline.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'uz-ride-search',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-ride-search.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'uz-ride-tracking',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-ride-tracking.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'uz-ride-search-small',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-ride-search-small.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'uz-non-accepted-rides',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-non-accepted-rides.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-create-new-itinerary',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-create-new-itinerary.svg')
    );

    this.matIconRegistry.addSvgIcon('uz-riders', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-riders.svg'));

    this.matIconRegistry.addSvgIcon(
      'uz-active-drivers',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-active-drivers.svg')
    );

    this.matIconRegistry.addSvgIcon('uz-reports', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-reports.svg'));

    this.matIconRegistry.addSvgIcon(
      'uz-upload-itineraries',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-upload-itineraries.svg')
    );

    this.matIconRegistry.addSvgIcon('uz-drivers', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-drivers.svg'));

    this.matIconRegistry.addSvgIcon(
      'uz-configuration',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-configuration.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-fare-review-queue',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-fare-review-queue.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-providers',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-providers.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-wav-dispatch',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-wav-dispatch.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-transit-program-map',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-transit-program-map.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-driver-onboarding',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-driver-onboarding.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-drivers-small',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-drivers-small.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-random-substance-screening',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-random-substance-screening.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-pencil-edit',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-pencil-edit.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-destination',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-destination.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-checkered-flag',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-checkered-flag.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-trash-outline',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-trash-outline.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-help-circle-outline',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-help-circle-outline.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-credit-card-issuer-mastercard',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-credit-card-issuer-mastercard.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-credit-card-issuer-visa',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-credit-card-issuer-visa.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-credit-card-issuer-amex',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-credit-card-issuer-amex.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-credit-card-issuer-dinersclub',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-credit-card-issuer-dinersclub.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-credit-card-issuer-discover',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-credit-card-issuer-discover.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-credit-card-issuer-jcb',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-credit-card-issuer-jcb.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-credit-card-issuer-default',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-credit-card-issuer-default.svg')
    );

    this.matIconRegistry.addSvgIcon(
      'uz-credit-card-outline',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/uz-icons/uz-credit-card-outline.svg')
    );

    // TODO: Move the above icons into this method
    this.registerMatIcons();
  }

  private registerMatIcons() {
    const iconNames: Array<string> = [
      'uz-active-drivers-lg',
      'uz-configuration-lg',
      'uz-create-new-itinerary-lg',
      'uz-dispatch-lg',
      'uz-driver-onboarding-lg',
      'uz-drivers-lg',
      'uz-fare-review-queue-lg',
      'uz-lyft-lg',
      'uz-non-accepted-rides-lg',
      'uz-providers-lg',
      'uz-random-substance-screening-lg',
      'uz-reports-lg',
      'uz-ride-search-lg',
      'uz-ride-tracking-lg',
      'uz-riders-lg',
      'uz-transit-program-map-lg',
      'uz-upload-itineraries-lg',
      'uz-wav-dispatch-lg',
      'uz-open',
      'uz-open-fill'
    ];

    for (const iconName of iconNames) {
      this.matIconRegistry.addSvgIcon(iconName, this.domSanitizer.bypassSecurityTrustResourceUrl(`../assets/uz-icons/${iconName}.svg`));
    }
  }

  private systemEventHandler = (systemEvent: SystemEvent) => {
    this.handleSystemEvent(systemEvent);
  };

  private handleSystemEvent(systemEvent: SystemEvent) {
    let whence: string;
    if (isNullOrUndefined(this.route.snapshot.queryParamMap.get('whence'))) {
      whence = window.location.href;
    } else {
      whence = this.route.snapshot.queryParamMap.get('whence');
    }

    if (systemEvent.code === SystemEventConstants.SYSTEM_MAINTENANCE) {
      this.router.navigate(['maintenance-mode'], { queryParams: { whence: whence } });
    } else if (systemEvent.code === SystemEventConstants.VERSION_DEPRECATED) {
      this.router.navigate(['deprecation-mode'], { queryParams: { whence: whence } });
    } else if (systemEvent.code === SystemEventConstants.SYSTEM_MAINTENANCE_ENDED) {
      this.router.navigateByUrl('/login');
    }
  }

  private initBannerInterval() {
    window.clearInterval(this.bannerInterval);
    this.bannerInterval = window.setInterval(() => {
      this.banners$ = !this.authDataService.isLoggedIn
        ? null
        : this.bannerService.listBannersByApp(
            AppConstants.CRM_BUNDLE_IDENTIFIER,
            this.appConfiguration.version,
            ReleasePlatformConstants.RELEASE_PLATFORM_CODE_WEB
          );
    }, this.appConfiguration.listPollingSeconds * 1000);
  }
}
